import { useLingui } from '@lingui/react/macro'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useUserStore } from '@tshared/stores/user.store'
import { Col, Row } from 'antd'
import React, { useEffect } from 'react'

import { Authentication } from '@/rbac'
import { useSupervisorStats } from '@/routes/-features/supervisor-dashboard/hooks'
import {
  CallsVolumeChart,
  HandledRejectedRate,
  InboundCallStatistics,
  KpiCards,
  OperatorStatusPanel,
} from '@/routes/-features/supervisor-dashboard/statistics'
import type { RingItem } from '@/routes/-features/supervisor-dashboard/statistics/InboundCallStatistics'
import type { KpiItem } from '@/routes/-features/supervisor-dashboard/statistics/KpiCards'
import { cn } from '@/utils/cn'

import '../dashboard/-styles.css'
import GlobalCallQueue from './-components/GlobalCallQueue'
import { Header } from './-components/Header.tsx'
import OperatorListPanel from './-components/OperatorListPanel'

const MONITOR_VARS = {
  '--stats-card-bg': '#ffffff',
  '--stats-card-border': '#e5e7eb',
  '--stats-text': '#1f2937',
  '--stats-text-secondary': '#6b7280',
  '--stats-bar-track': '#f3f4f6',
} as React.CSSProperties

export const Route = createFileRoute('/monitor')({
  component: MonitorPage,
  beforeLoad: async () => {
    // check user auth
    const user = useUserStore.getState().user
    if (!user?.accessToken) {
      return redirect({
        to: '/auth/sign-in',
        search: { redirect: '/monitor' },
      })
    }
    return
  },
})

function MonitorPage() {
  const { t } = useLingui()
  const {
    metrics,
    operators,
    kpiItems,
    operatorStatuses,
    inboundStatsItems,
    personalQueues,
    globalQueueCalls,
    operatorTotal,
  } = useSupervisorStats({ operatorSort: true })

  useEffect(() => {
    document.title = 'ILOS - Monitor'
  }, [])

  const kpiItemsWithLabels = React.useMemo(() => {
    const labels: Record<string, string> = {
      kpi_totalCallsToday: t`Total Calls Today`,
      kpi_totalCallsLastHour: t`Total Calls Last Hour`,
      kpi_callsInProgress: t`Calls In Progress`,
      kpi_avgResponseTime: t`Avg Response Time`,
      kpi_liveGlobalQueueLength: t`Global Queue`,
    }
    return kpiItems.map((i): KpiItem => ({ ...i, label: labels[i.labelKey] ?? i.labelKey }))
  }, [kpiItems, t])

  const inboundStatsItemsWithLabels = React.useMemo(() => {
    const labels: Record<string, string> = {
      inbound_answerTime60s: t`Answered ≤60s`,
      inbound_answerTime180s: t`Answered ≤180s`,
      inbound_missedRateToday: t`Missed Rate`,
    }
    return inboundStatsItems.map(
      (i): RingItem => ({ ...i, label: labels[i.labelKey] ?? i.labelKey }),
    )
  }, [inboundStatsItems, t])

  return (
    <Authentication require={'arc:supervisor:*'} failMode={'empty'}>
      <main
        style={MONITOR_VARS}
        className={cn('monitor flex h-dvh w-screen flex-col overflow-auto')}
      >
        <h1 className="sr-only">Monitor</h1>
        <Header />
        <div className={'h-full min-h-0 flex-1 flex-col p-2.5'}>
          <Row className={'h-full flex-1'} gutter={10}>
            <Col span={5} className={'h-full overflow-auto'}>
              <OperatorListPanel
                operators={operators?.operators ?? []}
                personalQueues={personalQueues}
                total={operatorTotal}
              />
            </Col>
            <Col span={14}>
              <div className={'flex h-full flex-col gap-2.5'}>
                <div>
                  <KpiCards items={kpiItemsWithLabels} />
                </div>
                <CallsVolumeChart
                  inbound={metrics?.volumes.inbound ?? []}
                  outbound={metrics?.volumes.outbound ?? []}
                  className={'flex-1'}
                />
                <Row gutter={10}>
                  <Col span={12}>
                    <OperatorStatusPanel statuses={operatorStatuses} />
                  </Col>
                  <Col span={12}>
                    <div className="flex flex-1 flex-col justify-between gap-4">
                      <InboundCallStatistics
                        items={inboundStatsItemsWithLabels}
                        className="flex-1"
                      />
                      <HandledRejectedRate
                        handledRate={metrics?.today.rates.handled_rate ?? 0}
                        rejectedRate={metrics?.today.rates.rejected_rate ?? 0}
                        className="shrink-0"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col span={5}>
              <GlobalCallQueue calls={globalQueueCalls} />
            </Col>
          </Row>
        </div>
      </main>
    </Authentication>
  )
}
